import React, { useState, useCallback } from "react";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import UploadFileIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import { useLazyQuery } from "@apollo/client";
import { ProductInput } from "../../input/ProductInput/ProductInput";
import { UploadDialog } from "./UploadDialog/UploadDialog";
import { ProductFilterProps, ProductOption } from "./types";
import { TEXT, EXPORT_CSV_HEADERS, MAX_PRODUCTS_LIMIT } from "./constants";
import { SIMPLIFIED_PRODUCT_ID_QUERY } from "../../../apis/taxonomy/queries";
import { ProductsQueryPayload } from "../../../types/taxonomy";
import { useSnackbar } from "../../Snackbar";
import { RotatingBorder } from "../../RotatingBorder";
import { TAXONOMY_CLIENT } from "../../../utils/consts";
import CollectionSelect from "../../CollectionSelect/CollectionSelect";

/**
 * ProductFilter component that integrates manual product search with CSV upload functionality
 * using an inline IconButton approach instead of radio toggles.
 */
export const ProductFilter: React.FC<ProductFilterProps> = ({
  onProductsSelect,
  selectedProductIds = [],
  uploadProps,
  highlighted,
  autoCompleteProps,
  disableCollectionSelect,
}) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  // Query for fetching product details for export
  const [fetchProductsForExport] = useLazyQuery<ProductsQueryPayload>(
    SIMPLIFIED_PRODUCT_ID_QUERY,
    { context: { clientName: TAXONOMY_CLIENT } }
  );

  // Handle products selection from ProductInput
  const handleProductsSelect = useCallback(
    (products: ProductOption[]) => {
      onProductsSelect({
        ids: products.map((product) => Number(product.value)),
        inclusionMethod: "ANY_PRESENT",
      });
    },
    [onProductsSelect]
  );

  // Handle successful upload from dialog
  const handleUploadSuccess = useCallback(
    (productIds: number[]) => {
      // Replace existing selections with uploaded products
      onProductsSelect({
        ids: productIds,
        inclusionMethod: "ANY_PRESENT",
      });

      // Only show success message if not explicitly disabled
      if (uploadProps?.showSuccessMessage) {
        showSuccess(TEXT.SNACKBAR_SUCCESS(productIds.length));
      }
      setUploadDialogOpen(false);
    },
    [onProductsSelect, showSuccess, uploadProps?.showSuccessMessage]
  );

  // Handle export of selected products
  const handleExport = useCallback(async () => {
    if (selectedProductIds.length === 0) return;

    setExportLoading(true);
    try {
      // Fetch product details
      const { data } = await fetchProductsForExport({
        variables: { ids: selectedProductIds },
      });

      const products = data?.product?.data;

      if (products) {
        // Create CSV content
        const csvRows = [
          EXPORT_CSV_HEADERS.join(","), // Headers
          ...products.map((product) =>
            [
              `"${product.name.replace(/"/g, '""')}"`, // Escape quotes in product name
              product.id,
            ].join(",")
          ),
        ];

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `products_export_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showSuccess(TEXT.SNACKBAR_EXPORT_SUCCESS(products.length));
      }
    } catch (error) {
      console.error("Export failed:", error);
      showError("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  }, [selectedProductIds, fetchProductsForExport, showSuccess, showError]);

  const renderProductInput = () => (
    <ProductInput
      onProductsSelect={handleProductsSelect}
      selectedProductIds={selectedProductIds}
      placeholder="Search products..."
      autoCompleteProps={autoCompleteProps}
    />
  );

  return (
    <Stack direction="column" gap={0.5}>
      <Stack direction="row" alignItems="center">
        <Stack id="product-input-container" flex={1} direction="column">
          {highlighted ? (
            <RotatingBorder borderRadius="4px">
              {renderProductInput()}
            </RotatingBorder>
          ) : (
            renderProductInput()
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} mx={1}>
          <Tooltip
            title={
              <Box sx={{ whiteSpace: "pre-line" }}>
                {TEXT.IMPORT_TOOLTIP_EXTENDED_BUTTON(
                  uploadProps?.noProductsLimit ? undefined : MAX_PRODUCTS_LIMIT
                )}
              </Box>
            }
            arrow
            placement="top"
          >
            <IconButton
              color="primary"
              onClick={() => setUploadDialogOpen(true)}
              size="small"
              disableRipple
              sx={{
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <UploadFileIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              <Box sx={{ whiteSpace: "pre-line" }}>
                {selectedProductIds.length > 0
                  ? TEXT.EXPORT_TOOLTIP_EXTENDED(selectedProductIds.length)
                  : TEXT.EXPORT_NO_SELECTION}
              </Box>
            }
            arrow
            placement="top"
          >
            <span>
              <Badge
                badgeContent={selectedProductIds.length}
                color="primary"
                max={999}
                showZero={false}
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.75rem",
                    minWidth: "16px",
                    height: "16px",
                    padding: "0 4px",
                  },
                }}
              >
                <IconButton
                  color="primary"
                  onClick={handleExport}
                  disabled={selectedProductIds.length === 0 || exportLoading}
                  size="small"
                  disableRipple
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Badge>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {!disableCollectionSelect && (
        <CollectionSelect
          placeholder="Search collections"
          type="product"
          onSelect={({ ids }) =>
            onProductsSelect({
              ids: [
                ...selectedProductIds,
                ...(Array.isArray(ids) ? ids : [])
                  .map((id) => Number(id))
                  .filter((id) => Number.isFinite(id))
                  .filter((id) => !selectedProductIds.includes(id)),
              ],
              inclusionMethod: "ANY_PRESENT",
            })
          }
        />
      )}

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={handleUploadSuccess}
        existingProductIds={selectedProductIds}
        uploadProps={uploadProps}
      />
    </Stack>
  );
};
